(function () {
  'use strict';

  // ===== Background video bootstrap (keep video happy) =====
  const bgVideo = document.getElementById('bg-video');

  if (bgVideo) {
    bgVideo.muted = true;
    bgVideo.playsInline = true;

    const tryPlay = () => {
      try {
        const p = bgVideo.play();
        if (p && typeof p.then === 'function') {
          p.catch(() => {
            // ignore autoplay rejection, we'll retry on interaction
          });
        }
      } catch (e) {
        // ignore
      }
    };

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      tryPlay();
    } else {
      document.addEventListener('DOMContentLoaded', tryPlay, { once: true });
    }

    ['click', 'touchstart'].forEach((evtName) => {
      window.addEventListener(
        evtName,
        () => {
          tryPlay();
        },
        { once: true, passive: true }
      );
    });
  }

  // ===== Orbit logic =====

  const cfg = {
    shared: {
      radiusX: 0.38,
      radiusY: 0.2,
      baseSpeed: 2.5
    }
  };

  const stage = document.querySelector('.orbit-stage');
  if (!stage) return;

  const items = Array.from(document.querySelectorAll('.orbit-item'));
  if (!items.length) return;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const state = {
    baseAngle: 0,
    rotationDirection: 1,
    stageSize: 0,
    isDragging: false,
    lastPointerAngle: 0,
    spinVelocity: 0,
    startX: 0,
    startY: 0,
    totalDistance: 0,
    startTime: 0,
    clickedItem: null
  };

  const DRAG_THRESHOLD = isMobile ? 15 : 10;
  const TAP_TIME_THRESHOLD = 300;
  const CLICK_VELOCITY_THRESHOLD = 0.02;
  const DRAG_MULT = 0.9;
  const MAX_SPIN_VELOCITY = 0.12;
  const MAX_ROTATION_SPEED = 0.25;
  const INERTIA_DECAY = 0.99;
  const MIN_VELOCITY = 0.0001;

  let smoothedVelocity = 0;
  let lastTimestamp = 0;

  let precalcPositions = items.map(() => ({
    x: 0,
    y: 0,
    scale: 1,
    opacity: 1,
    zIndex: 50
  }));

  function measureStage() {
    state.stageSize = Math.min(stage.offsetWidth, stage.offsetHeight);
  }

  function getPointerAngle(evt) {
    const rect = stage.getBoundingClientRect();
    const cx = rect.left + rect.width * 0.5;
    const cy = rect.top + rect.height * 0.5;
    const px = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const py = evt.touches ? evt.touches[0].clientY : evt.clientY;
    return Math.atan2(py - cy, px - cx);
  }

  function getPointerCoords(evt) {
    return {
      x: evt.touches ? evt.touches[0].clientX : evt.clientX,
      y: evt.touches ? evt.touches[0].clientY : evt.clientY
    };
  }

  function calculatePositions() {
    const radiusX = cfg.shared.radiusX * state.stageSize;
    const radiusY = cfg.shared.radiusY * state.stageSize;

    for (let i = 0; i < items.length; i++) {
      const offset = parseFloat(items[i].dataset.offset) || 0;
      const sizeMultiplier = parseFloat(items[i].dataset.size) || 1;

      const angle = state.baseAngle + offset;
      const cosAngle = Math.cos(angle);
      const sinAngle = Math.sin(angle);

      const x = cosAngle * radiusX;
      const verticalStretch = 1 + sinAngle * 0.11;
      const y = sinAngle * radiusY * verticalStretch;

      const depthFactor = sinAngle * 0.7 + Math.abs(cosAngle) * 0.3;
      const baseScale = (0.45 + depthFactor * 0.45) * 2;
      const scale = baseScale * sizeMultiplier;
      const opacity = 0.25 + (depthFactor * 0.5 + 0.5) * 0.75;

      const zIndex =
        sinAngle < 0
          ? (49 - ((sinAngle - 0) / -1) * 48) | 0
          : (51 + ((sinAngle - 0) / 1) * 49) | 0;

      precalcPositions[i].x = x;
      precalcPositions[i].y = y;
      precalcPositions[i].scale = scale;
      precalcPositions[i].opacity = opacity;
      precalcPositions[i].zIndex = zIndex;
    }
  }

  function renderItems() {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const pos = precalcPositions[i];

      if (isMobile) {
        const xPx = Math.round(pos.x * 100) / 100;
        const yPx = Math.round(pos.y * 100) / 100;
        const scaleVal = Math.round(pos.scale * 100) / 100;
        const opacityVal = Math.round(pos.opacity * 100) / 100;

        item.style.transform =
          'translate3d(calc(-50% + ' +
          xPx +
          'px), calc(-50% + ' +
          yPx +
          'px), 0) scale(' +
          scaleVal +
          ')';
        item.style.opacity = opacityVal;
      } else {
        item.style.transform =
          'translate3d(calc(-50% + ' +
          pos.x.toFixed(2) +
          'px), calc(-50% + ' +
          pos.y.toFixed(2) +
          'px), 0) scale(' +
          pos.scale.toFixed(4) +
          ')';
        item.style.opacity = pos.opacity.toFixed(3);
      }

      item.style.zIndex = pos.zIndex;
    }
  }

  function animate(timestamp) {
    if (!lastTimestamp) lastTimestamp = timestamp;
    const deltaTime = Math.min(timestamp - lastTimestamp, 33.33);
    lastTimestamp = timestamp;

    if (!state.isDragging) {
      const sinAngle = Math.sin(state.baseAngle);
      const speedMod = 1 - Math.pow(sinAngle, 2) * 0.65;

      const autoRot =
        state.rotationDirection *
        cfg.shared.baseSpeed *
        0.001 *
        deltaTime *
        speedMod;

      const cappedRot = Math.max(
        -MAX_ROTATION_SPEED,
        Math.min(MAX_ROTATION_SPEED, autoRot)
      );
      state.baseAngle += cappedRot;

      if (Math.abs(smoothedVelocity) > MIN_VELOCITY) {
        const applied = smoothedVelocity * deltaTime * 0.06;
        const cappedApplied = Math.max(
          -MAX_ROTATION_SPEED,
          Math.min(MAX_ROTATION_SPEED, applied)
        );
        state.baseAngle += cappedApplied;

        smoothedVelocity *= Math.pow(INERTIA_DECAY, deltaTime / 16.67);

        if (Math.abs(smoothedVelocity) > 0.001) {
          state.rotationDirection = smoothedVelocity > 0 ? 1 : -1;
        }
      } else {
        smoothedVelocity = 0;
      }
    }

    calculatePositions();
    renderItems();
    requestAnimationFrame(animate);
  }

  function handleDragStart(evt) {
    const target = evt.target;

    if (target.closest('.cta-button')) {
      return;
    }

    const coords = getPointerCoords(evt);
    state.isDragging = true;
    state.lastPointerAngle = getPointerAngle(evt);
    smoothedVelocity = 0;
    state.startX = coords.x;
    state.startY = coords.y;
    state.totalDistance = 0;
    state.startTime = Date.now();
    state.clickedItem = target.closest('.orbit-item, .orbit-center');

    items.forEach(item => item.classList.add('is-dragging'));
    // ❌ no preventDefault here – mobile needs it to generate a click
  }


  function handleDragMove(evt) {
    if (!state.isDragging) return;

    const coords = getPointerCoords(evt);
    const currentAngle = getPointerAngle(evt);

    const dx = coords.x - state.startX;
    const dy = coords.y - state.startY;
    state.totalDistance += Math.sqrt(dx * dx + dy * dy);
    state.startX = coords.x;
    state.startY = coords.y;

    let angleDelta = currentAngle - state.lastPointerAngle;
    if (angleDelta > Math.PI) angleDelta -= 6.28318;
    if (angleDelta < -Math.PI) angleDelta += 6.28318;

    const cappedDelta = Math.max(-0.15, Math.min(0.15, angleDelta));
    state.baseAngle += cappedDelta * DRAG_MULT;
    smoothedVelocity = cappedDelta * DRAG_MULT;
    smoothedVelocity = Math.max(
      -MAX_SPIN_VELOCITY,
      Math.min(MAX_SPIN_VELOCITY, smoothedVelocity)
    );

    if (angleDelta !== 0) {
      state.rotationDirection = angleDelta > 0 ? 1 : -1;
    }

    state.lastPointerAngle = currentAngle;
    evt.preventDefault();
  }

  function handleDragEnd(evt) {
    if (!state.isDragging) return;

    const duration = Date.now() - state.startTime;
    const wasDragging = state.totalDistance > DRAG_THRESHOLD;
    const wasQuickTap = duration < TAP_TIME_THRESHOLD;
    const isStillEnough =
      Math.abs(smoothedVelocity) < CLICK_VELOCITY_THRESHOLD;

    state.isDragging = false;
    state.totalDistance = 0;
    items.forEach((item) => item.classList.remove('is-dragging'));

    const shouldAllowClick = !wasDragging && wasQuickTap && isStillEnough;

    if (!shouldAllowClick) {
      evt.preventDefault();
      if (wasDragging) {
        smoothedVelocity *= isMobile ? 1.6 : 1.4;
        smoothedVelocity = Math.max(
          -MAX_SPIN_VELOCITY,
          Math.min(MAX_SPIN_VELOCITY, smoothedVelocity)
        );
      }
    }

    state.clickedItem = null;
  }

  // NOTE: no custom click handler now – normal anchor clicks go through

  window.addEventListener('mousedown', handleDragStart, false);
  window.addEventListener('touchstart', handleDragStart, { passive: false });
  window.addEventListener('mousemove', handleDragMove, false);
  window.addEventListener('touchmove', handleDragMove, { passive: false });
  window.addEventListener('mouseup', handleDragEnd, false);
  window.addEventListener('touchend', handleDragEnd, false);

  new ResizeObserver(() => {
    clearTimeout(window.resizeTimeout);
    window.resizeTimeout = setTimeout(() => {
      measureStage();
      calculatePositions();
      renderItems();
    }, 150);
  }).observe(stage);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      lastTimestamp = 0;
    }
  });

  measureStage();
  items.forEach((item) => {
    item.style.left = '50%';
    item.style.top = '50%';
  });
  calculatePositions();
  renderItems();
  requestAnimationFrame(animate);
})();
